import { useEffect, useRef, useState } from 'react'
import { IconButton } from './IconButton'
import { CheckIcon, CopyIcon } from './Icons'

type CopyButtonProps = {
  value: string
  /** Accessible name, e.g. "Copy invite link". */
  label: string
  size?: number
}

/**
 * The clipboard affordance, used wherever a link or code is shown. A copy can fail for reasons the
 * page cannot fix (no secure context, permission denied), so the failure is surfaced next to the
 * button instead of swallowed.
 */
export function CopyButton({ value, label, size = 36 }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => clearTimeout(timer.current)
  }, [])

  const reset = () => {
    clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setState('idle'), 2000)
  }

  const copy = () => {
    const clipboard = navigator.clipboard
    if (!clipboard) {
      setState('failed')
      reset()
      return
    }
    clipboard.writeText(value).then(
      () => {
        setState('copied')
        reset()
      },
      () => {
        setState('failed')
        reset()
      },
    )
  }

  const title = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed — select it manually' : label
  return (
    <IconButton label={title} size={size} onClick={copy} className="shrink-0">
      {state === 'copied' ? (
        <CheckIcon size={16} className="text-accent" />
      ) : (
        <CopyIcon size={16} className={state === 'failed' ? 'text-danger' : undefined} />
      )}
    </IconButton>
  )
}
